import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useActivityParent } from "PFApp/activities/show/hooks/use_activity_parent";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import {
  AUDIT_ENGAGEMENT_ID_QUERY_PARAM,
  AUDIT_PLANNER_PAGE_QUERY_PARAM
} from "PFApp/audit_planner/audit_planner.utils";
import { useTemplate } from "PFApp/hooks";
import { Breadcrumbs } from "PFComponents/breadcrumbs";
import { BreadcrumbItem } from "PFComponents/breadcrumbs/breadcrumb_item";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";

import { useTranslatedActivityType } from "../../../../hooks/use_translated_activity_type";
import css from "../header.module.scss";

export const WorkflowBreadcrumbs = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const [searchParams] = useSearchParams();

  const activity = useCurrentActivity();
  const { isAudit, isRole, isEngagement } = useActivityShowContext();
  const template = useTemplate(activity.template_id);
  const parentActivity = useActivityParent(activity);

  const parentActivityTypeLabel = useTranslatedActivityType(parentActivity!);
  const currentActivityTypeLabel = useTranslatedActivityType(activity);

  const auditPlannerPage = searchParams.get(AUDIT_PLANNER_PAGE_QUERY_PARAM) ?? "1";

  const rootLinkData = useMemo(() => {
    if (isAudit) {
      const auditEngagementId = isRole ? parentActivity?.id : activity.id;

      if (auditEngagementId) {
        const queryParams = new URLSearchParams({
          [AUDIT_ENGAGEMENT_ID_QUERY_PARAM]: `${auditEngagementId}`,
          [AUDIT_PLANNER_PAGE_QUERY_PARAM]: auditPlannerPage
        });
        return { path: `/audit_planner?${queryParams.toString()}`, label: t("auditPlanner") };
      }

      return { path: "/audit_planner", label: t("auditPlanner") };
    }

    if (isRole || isEngagement) {
      return { path: `/workflow/${isRole ? "roles" : "engagements"}`, label: t("workflow") };
    }
  }, [isAudit, isRole, isEngagement, t, parentActivity?.id, activity.id, auditPlannerPage]);

  if (!rootLinkData) {
    return null;
  }

  const parentActivitySearch = new URLSearchParams({
    origin_id: `${activity.id}`,
    origin_type: template?.key ?? ""
  });

  if (isAudit) {
    parentActivitySearch.set(AUDIT_PLANNER_PAGE_QUERY_PARAM, auditPlannerPage);
  }

  return (
    <Breadcrumbs>
      <BreadcrumbItem
        className={css.workflowMainBreadcrumb}
        label={rootLinkData.label}
        href={rootLinkData.path}
      />
      {parentActivity && (
        <BreadcrumbItem
          label={`${parentActivityTypeLabel}: ${parentActivity.name}`}
          href={`/activities/${parentActivity.id}`}
          search={parentActivitySearch}
          state={{ utmSource: (location?.state as any)?.utmSource }}
        />
      )}
      <BreadcrumbItem label={`${currentActivityTypeLabel}: ${activity.name}`} isCurrent />
    </Breadcrumbs>
  );
};
